import {
  FloatingFocusManager,
  autoUpdate,
  flip,
  offset,
  size,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import dynamic from 'next/dynamic';
import { ComponentProps, ReactNode, forwardRef, useRef, useState } from 'react';

import { IconCircleCrossFilled } from 'src/general/Icons/IconCircleCrossFilled';
import { IconSearch } from 'src/general/Icons/IconSearch';

import { ButtonIcon } from '../Button/ButtonIcon';
import { ConditionalWrapper } from '../ConditionalWrapper/ConditionalWrapper';
import { Input } from '../forms/Input/Input';
import styles from './Autocomplete.module.scss';

/**
 * Adapted from floating-ui Autocomplete example: https://floating-ui.com/docs/react-examples
 */

export type AutocompleteItem<ResultType> = {
  key: string;
  label: ReactNode;
  value: ResultType;
};

type Props<ResultType> = {
  getItems: (search: string) => Promise<AutocompleteItem<ResultType>[]>;
  onSelectItem: (value: ResultType) => void;
  disablePortal?: boolean;
} & Omit<ComponentProps<typeof Input>, 'value' | 'onChange'>;

const FloatingPortal = dynamic(() => import('@floating-ui/react').then((mod) => mod.FloatingPortal), {
  ssr: false,
});

export function Autocomplete<ResultType>({ onSelectItem, getItems, disablePortal, ...props }: Props<ResultType>) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [items, setItems] = useState<AutocompleteItem<ResultType>[]>([]);

  const listRef = useRef<Array<HTMLElement | null>>([]);

  const { refs, floatingStyles, context } = useFloating<HTMLInputElement>({
    whileElementsMounted: autoUpdate,
    open,
    onOpenChange: setOpen,
    middleware: [
      flip({ padding: 10 }),
      offset(4),
      size({
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${availableHeight}px`,
          });
        },
        padding: 10,
      }),
    ],
  });

  const role = useRole(context, { role: 'listbox' });
  const dismiss = useDismiss(context);
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true,
    loop: true,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([role, dismiss, listNav]);

  function onChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setInputValue(value);

    if (value) {
      setOpen(true);
      setActiveIndex(0);
      getItems(value)
        .then((items) => setItems(items))
        .catch(() => setItems([]));
    } else {
      setOpen(false);
    }
  }

  return (
    <>
      <div className={classNames(styles.autocomplete, 'autocomplete')}>
        <Input
          {...getReferenceProps({
            ref: refs.setReference,
            onChange,
            'aria-autocomplete': 'list',
            onKeyDown(event) {
              if (event.key === 'Enter' && activeIndex != null && items[activeIndex]) {
                event.preventDefault();
                if (activeIndex < items.length) {
                  onSelectItem(items[activeIndex].value);
                  setInputValue('');
                }
                setActiveIndex(null);
                setOpen(false);
              } else if (event.key === 'Escape') {
                setInputValue('');
              }
            },
          })}
          {...props}
          className={classNames(styles.autocompleteInput, 'autocompleteInput')}
          value={inputValue}
        />
        <ButtonIcon
          className={classNames(styles.autocompleteEndIconWrapper, 'autocompleteEndIconWrapper')}
          title="Clear"
          variant="tertiary"
          size="small"
          styling="round"
          Icon={isEmpty(inputValue) ? IconSearch : IconCircleCrossFilled}
          onClick={(event: { preventDefault: () => void }) => {
            event.preventDefault();
            setInputValue('');
          }}
          disabled={isEmpty(inputValue)}
        />
      </div>
      <ConditionalWrapper condition={!disablePortal} wrapper={portalWrapper}>
        {open && items.length > 0 && (
          <FloatingFocusManager context={context} initialFocus={-1} visuallyHiddenDismiss>
            <div
              {...getFloatingProps({
                ref: refs.setFloating,
                style: floatingStyles,
              })}
              className={classNames(styles.autocompleteTooltip, 'autocompleteTooltip')}
            >
              {items.map((item, index) => (
                <Item
                  {...getItemProps({
                    key: item.key,
                    ref(node) {
                      listRef.current[index] = node;
                    },
                    onClick() {
                      onSelectItem(item.value);
                      setInputValue('');
                      setOpen(false);
                      refs.domReference.current?.focus();
                    },
                  })}
                  active={activeIndex === index}
                >
                  {item.label}
                </Item>
              ))}
            </div>
          </FloatingFocusManager>
        )}
      </ConditionalWrapper>
    </>
  );
}

const portalWrapper: ComponentProps<typeof ConditionalWrapper>['wrapper'] = (children) => (
  <FloatingPortal>{children}</FloatingPortal>
);

interface ItemProps {
  children: React.ReactNode;
  active: boolean;
}

const Item = forwardRef<HTMLDivElement, ItemProps & React.HTMLProps<HTMLDivElement>>(
  ({ children, active, ...rest }, ref) => {
    const id = useId();
    return (
      <div
        ref={ref}
        role="option"
        id={id}
        aria-selected={active}
        className={classNames(styles.autocompleteItem, 'autocompleteItem', {
          [styles.autocompleteItemActive]: active,
          autocompleteItemActive: active,
        })}
        {...rest}
        style={{
          cursor: 'default',
          ...rest.style,
        }}
      >
        {children}
      </div>
    );
  },
);

Item.displayName = 'Item';
